import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/cartcontext";
import { useAuth } from "../context/AuthContext";
import PageHero from "../components/PageHero";
import heroImage from "../assets/zeamays.png";
import { formatPrice } from "../data/products";
import { getActiveProductsByIds } from "../services/storefrontProducts";
import { collectCartInventoryIssues } from "../utils/inventory";

const PRODUCTS_ROUTE = "/products";

const Cart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { cart, cartSubtotal, loading, syncing, errorMessage, removeFromCart, updateQuantity, clearCart } = useCart();
  const [inventoryProducts, setInventoryProducts] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadInventory = async () => {
      const productIds = [...new Set(cart.map((item) => item.productId).filter(Boolean))];

      if (!productIds.length) {
        if (!cancelled) {
          setInventoryProducts([]);
          setInventoryLoading(false);
        }
        return;
      }

      if (!cancelled) {
        setInventoryLoading(true);
      }

      try {
        const products = await getActiveProductsByIds(productIds);
        if (!cancelled) {
          setInventoryProducts(products);
        }
      } catch (_error) {
        if (!cancelled) {
          setInventoryProducts([]);
        }
      } finally {
        if (!cancelled) {
          setInventoryLoading(false);
        }
      }
    };

    loadInventory();

    return () => {
      cancelled = true;
    };
  }, [cart]);

  const inventoryIssues = useMemo(
    () => collectCartInventoryIssues(cart, inventoryProducts),
    [cart, inventoryProducts]
  );
  const checkoutBlocked = inventoryLoading || inventoryIssues.length > 0;

  useEffect(() => {
    const checkoutAfterLogin = new URLSearchParams(location.search).get("checkout") === "1";

    if (checkoutAfterLogin && user && !loading && cart.length === 0) {
      navigate("/checkout", { replace: true });
    }
  }, [cart.length, loading, location.search, navigate, user]);

  const checkout = () => {
    if (!user) {
      navigate("/signup", {
        state: {
          from: { pathname: "/checkout" },
          checkoutIntent: "signup",
        },
      });
      return;
    }

    if (!cart.length) {
      return;
    }

    if (checkoutBlocked) {
      return;
    }

    navigate("/checkout");
  };

  return (
    <div className="min-h-screen bg-[#f7fbef]">
      <PageHero
        eyebrow="Your cart"
        title="Review your basket before checkout."
        description="Make sure sizes and quantities look right, then head to checkout when you're ready."
        image={cart.length ? cart[0].image : heroImage}
        imageAlt="Cart preview"
        primaryAction={{ to: "/products", label: "Continue Shopping" }}
        secondaryAction={
          user
            ? { to: "/checkout", label: "Proceed to Checkout" }
            : {
                to: "/signup",
                label: "Sign up to checkout",
                state: {
                  from: { pathname: "/checkout" },
                  checkoutIntent: "signup",
                },
              }
        }
        stats={[
          { label: "Items", value: `${cart.length}` },
          { label: "Subtotal", value: formatPrice(cartSubtotal) },
          { label: "Stock", value: inventoryLoading ? "Checking..." : "Verified" },
          { label: "Status", value: user ? "Signed in" : "Guest" },
        ]}
        compact
      />

      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Your Cart</h1>
            <p className="text-sm text-gray-600">
              {user
                ? "Review quantities before checkout."
                : "You can shop and manage your cart as a guest. Sign in only when you are ready to checkout."}
            </p>
          </div>
          {cart.length > 0 ? (
            <button
              onClick={clearCart}
              className="rounded-lg border border-red-200 px-4 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50"
            >
              Clear cart
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
            <p className="text-lg text-gray-700">Loading your cart...</p>
          </div>
        ) : null}

        {errorMessage ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errorMessage}
          </div>
        ) : null}

        {inventoryIssues.length > 0 ? (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <p className="font-semibold">Checkout blocked until your cart is updated.</p>
            <ul className="mt-2 space-y-1">
              {inventoryIssues.map((issue) => (
                <li key={`${issue.productId}-${issue.productName}`}>{issue.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        {!loading && cart.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center">
            <p className="mb-4 text-lg text-gray-700">No items in cart.</p>
            <div className="flex items-center justify-center gap-4">
              <Link
                to={PRODUCTS_ROUTE}
                className="rounded-lg bg-red-700 px-5 py-3 font-semibold text-white transition hover:bg-green-300"
              >
                Continue shopping
              </Link>
              {!user ? (
                <Link to="/login" className="font-semibold text-red-700 hover:text-green-600">
                  Sign in
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}

        {!loading && cart.length > 0 ? (
          <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
            <ul className="space-y-4">
              {cart.map((item) => (
                <li key={item.id} className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm">
                  <img src={item.image} alt={item.name} className="h-20 w-20 rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <h2 className="font-semibold text-gray-900">{item.name}</h2>
                    {item.selectedSize ? <p className="text-sm text-gray-500">Size: {item.selectedSize}</p> : null}
                    <p className="text-sm text-gray-500">{formatPrice(item.price)} each</p>
                    {inventoryIssues.find((issue) => issue.productId === item.productId) ? (
                      <p className="mt-1 text-sm font-medium text-amber-700">
                        {inventoryIssues.find((issue) => issue.productId === item.productId)?.message}
                      </p>
                    ) : null}
                  </div>
                  <input
                    type="number"
                    value={item.quantity}
                    min="1"
                    onChange={(event) => updateQuantity(item.id, Number(event.target.value) || 1)}
                    className="w-20 rounded-lg border border-gray-300 px-3 py-2"
                  />
                  <div className="w-28 text-right font-semibold text-gray-900">
                    {formatPrice(item.price * item.quantity)}
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-50"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            <div className="h-fit rounded-2xl bg-white p-6 shadow-sm">
              <h2 className="mb-4 text-xl font-semibold text-gray-900">Summary</h2>
              <div className="mb-6 flex items-center justify-between text-base text-gray-700">
                <span>Total</span>
                <span className="font-bold text-gray-900">{formatPrice(cartSubtotal)}</span>
              </div>
              {syncing ? <p className="mb-4 text-sm text-gray-500">Syncing cart...</p> : null}
              {inventoryLoading ? <p className="mb-4 text-sm text-amber-700">Checking live stock...</p> : null}
              <button
                onClick={checkout}
                disabled={checkoutBlocked}
                className="w-full rounded-xl bg-red-700 px-4 py-3 font-semibold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                {user ? "Proceed to checkout" : "Sign up or sign in to checkout"}
              </button>
              <Link
                to={PRODUCTS_ROUTE}
                className="mt-3 block w-full rounded-xl border border-red-200 px-4 py-3 text-center font-semibold text-red-700 transition hover:bg-red-50"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default Cart;
